import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { FiPlus, FiSearch, FiEye, FiEdit, FiMail, FiDollarSign, FiFilter } from 'react-icons/fi';
import { FaFileInvoice } from 'react-icons/fa';

interface InvoiceListItem {
        id: string;
        invoice_number: string;
        invoice_date: string;
        patient_name: string;
        patient_id: number;
        doctor_name: string;
        total: number;
        amount_paid: number;
        balance: number;
        status: 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled';
        facility_name: string;
}

const InvoiceList: React.FC = () => {
        const navigate = useNavigate();
        const { token } = useAuth();
        const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
        const [loading, setLoading] = useState(true);
        const [searchTerm, setSearchTerm] = useState('');
        const [statusFilter, setStatusFilter] = useState<string>('all');
        const [dateFrom, setDateFrom] = useState('');
        const [dateTo, setDateTo] = useState('');
        const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());

        useEffect(() => {
                if (token) {
                        fetchInvoices();
                }
        }, [token, statusFilter, dateFrom, dateTo]);

        const fetchInvoices = async () => {
                try {
                        setLoading(true);
                        const params = new URLSearchParams();
                        if (statusFilter !== 'all') params.append('status', statusFilter);
                        if (dateFrom) params.append('date_from', dateFrom);
                        if (dateTo) params.append('date_to', dateTo);
                        if (searchTerm) params.append('search', searchTerm);

                        const url = `/billing/invoices/?${params.toString()}`;
                        const response = await apiCall(url, { method: 'GET' }, token!);

                        if (response.ok) {
                                const data = await response.json();
                                setInvoices(data.results || data);
                        }
                } catch (error) {
                        console.error('Error fetching invoices:', error);
                } finally {
                        setLoading(false);
                }
        };

        const handleSearch = () => {
                fetchInvoices();
        };

        const handleSelectAll = (checked: boolean) => {
                if (checked) {
                        setSelectedInvoices(new Set(invoices.map((inv) => inv.id)));
                } else {
                        setSelectedInvoices(new Set());
                }
        };

        const handleSelectInvoice = (id: string, checked: boolean) => {
                const newSelected = new Set(selectedInvoices);
                if (checked) {
                        newSelected.add(id);
                } else {
                        newSelected.delete(id);
                }
                setSelectedInvoices(newSelected);
        };

        const handleBulkEmail = async () => {
                if (selectedInvoices.size === 0) {
                        alert('Please select invoices to email');
                        return;
                }
                // TODO: Implement bulk email
                alert(`Sending ${selectedInvoices.size} invoice(s) via email`);
        };

        const getStatusBadgeClass = (status: InvoiceListItem['status']) => {
                switch (status) {
                        case 'paid':
                                return 'badge-success';
                        case 'partial':
                                return 'badge-warning';
                        case 'overdue':
                                return 'badge-error';
                        case 'sent':
                                return 'badge-info';
                        case 'cancelled':
                                return 'badge-ghost';
                        default:
                                return 'badge-ghost';
                }
        };

        const getTotalStats = () => {
                const total = invoices.reduce((sum, inv) => sum + inv.total, 0);
                const paid = invoices.reduce((sum, inv) => sum + inv.amount_paid, 0);
                const outstanding = invoices.reduce((sum, inv) => sum + inv.balance, 0);
                return { total, paid, outstanding };
        };

        const stats = getTotalStats();

        return (
                <div className="p-6">
                        {/* Header with Stats */}
                        <div className="mb-6">
                                <div className="flex justify-between items-center mb-4">
                                        <div>
                                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                                        <FaFileInvoice className="text-primary" />
                                                        Invoices
                                                </h1>
                                        </div>
                                        
                                        {/* Stats Cards */}
                                        <div className="flex gap-4">
                                                <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                        <FiDollarSign className="text-primary text-2xl" />
                                                        <span className="text-xs font-semibold text-base-content opacity-60">Total Invoiced:</span>
                                                        <span className="text-lg font-bold text-primary">${stats.total.toFixed(2)}</span>
                                                        <span className="text-xs text-base-content opacity-60">({invoices.length} invoices)</span>
                                                </div>
                                                <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                        <FiDollarSign className="text-success text-2xl" />
                                                        <span className="text-xs font-semibold text-base-content opacity-60">Amount Paid:</span>
                                                        <span className="text-lg font-bold text-success">${stats.paid.toFixed(2)}</span>
                                                        <span className="text-xs text-base-content opacity-60">(Collected)</span>
                                                </div>
                                                <div className="bg-base-200 rounded-lg px-4 py-2 flex items-center gap-2">
                                                        <FiDollarSign className="text-error text-2xl" />
                                                        <span className="text-xs font-semibold text-base-content opacity-60">Outstanding:</span>
                                                        <span className="text-lg font-bold text-error">${stats.outstanding.toFixed(2)}</span>
                                                        <span className="text-xs text-base-content opacity-60">(Pending)</span>
                                                </div>
                                        </div>

                                        <button
                                                onClick={() => navigate('/billing/invoices/new')}
                                                className="btn btn-primary gap-2"
                                        >
                                                <FiPlus /> Create Invoice
                                        </button>
                                </div>
                        </div>

                        {/* Filters */}
                        <div className="bg-base-200 rounded-lg p-4 mb-6">
                                <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-2 flex-1">
                                                <span className="text-sm font-medium whitespace-nowrap">Search</span>
                                                <div className="relative flex-1">
                                                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content opacity-40" />
                                                        <input
                                                                type="text"
                                                                placeholder="Patient name, invoice #..."
                                                                className="input input-bordered w-full pl-10"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                        />
                                                </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Status</span>
                                                <select
                                                        className="select select-bordered"
                                                        value={statusFilter}
                                                        onChange={(e) => setStatusFilter(e.target.value)}
                                                >
                                                        <option value="all">All Status</option>
                                                        <option value="draft">Draft</option>
                                                        <option value="sent">Sent</option>
                                                        <option value="partial">Partial Payment</option>
                                                        <option value="paid">Paid</option>
                                                        <option value="overdue">Overdue</option>
                                                        <option value="cancelled">Cancelled</option>
                                                </select>
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Date From</span>
                                                <input
                                                        type="date"
                                                        className="input input-bordered"
                                                        value={dateFrom}
                                                        onChange={(e) => setDateFrom(e.target.value)}
                                                />
                                        </div>

                                        <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium whitespace-nowrap">Date To</span>
                                                <input
                                                        type="date"
                                                        className="input input-bordered"
                                                        value={dateTo}
                                                        onChange={(e) => setDateTo(e.target.value)}
                                                />
                                        </div>
                                </div>
                        </div>

                        {/* Bulk Actions */}
                        {selectedInvoices.size > 0 && (
                                <div className="bg-primary text-primary-content rounded-lg shadow p-4 mb-4 flex justify-between items-center">
                                        <span className="font-medium">{selectedInvoices.size} invoice(s) selected</span>
                                        <div className="flex gap-2">
                                                <button onClick={handleBulkEmail} className="btn btn-sm btn-ghost gap-2">
                                                        <FiMail /> Email Selected
                                                </button>
                                                <button
                                                        onClick={() => setSelectedInvoices(new Set())}
                                                        className="btn btn-sm btn-ghost"
                                                >
                                                        Clear Selection
                                                </button>
                                        </div>
                                </div>
                        )}

                        {/* Table */}
                        <div className="bg-base-100 rounded-lg shadow overflow-hidden">
                                {loading ? (
                                        <div className="flex justify-center items-center p-12">
                                                <div className="loading loading-spinner loading-lg"></div>
                                        </div>
                                ) : invoices.length === 0 ? (
                                        <div className="text-center p-12">
                                                <p className="text-lg opacity-60">No invoices found</p>
                                                <button
                                                        onClick={() => navigate('/billing/invoices/new')}
                                                        className="btn btn-primary mt-4"
                                                >
                                                        Create First Invoice
                                                </button>
                                        </div>
                                ) : (
                                        <div className="overflow-x-auto">
                                                <table className="table w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>
                                                                                <input
                                                                                        type="checkbox"
                                                                                        className="checkbox"
                                                                                        checked={selectedInvoices.size === invoices.length}
                                                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                                                />
                                                                        </th>
                                                                        <th>Invoice #</th>
                                                                        <th>Date</th>
                                                                        <th>Patient</th>
                                                                        <th>Doctor</th>
                                                                        <th>Facility</th>
                                                                        <th className="text-right">Total</th>
                                                                        <th className="text-right">Paid</th>
                                                                        <th className="text-right">Balance</th>
                                                                        <th>Status</th>
                                                                        <th>Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {invoices.map((invoice) => (
                                                                        <tr key={invoice.id} className="hover">
                                                                                <td>
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                className="checkbox"
                                                                                                checked={selectedInvoices.has(invoice.id)}
                                                                                                onChange={(e) =>
                                                                                                        handleSelectInvoice(invoice.id, e.target.checked)
                                                                                                }
                                                                                        />
                                                                                </td>
                                                                                <td>
                                                                                        <a
                                                                                                href={`/billing/invoices/${invoice.id}`}
                                                                                                className="link link-primary font-medium"
                                                                                        >
                                                                                                {invoice.invoice_number}
                                                                                        </a>
                                                                                </td>
                                                                                <td>{invoice.invoice_date}</td>
                                                                                <td>
                                                                                        <a
                                                                                                href={`/patients/${invoice.patient_id}`}
                                                                                                className="link link-hover"
                                                                                        >
                                                                                                {invoice.patient_name}
                                                                                        </a>
                                                                                </td>
                                                                                <td>{invoice.doctor_name}</td>
                                                                                <td>{invoice.facility_name}</td>
                                                                                <td className="text-right font-medium">
                                                                                        ${invoice.total.toFixed(2)}
                                                                                </td>
                                                                                <td className="text-right text-success">
                                                                                        ${invoice.amount_paid.toFixed(2)}
                                                                                </td>
                                                                                <td className="text-right">
                                                                                        <span
                                                                                                className={
                                                                                                        invoice.balance > 0 ? 'text-error font-medium' : ''
                                                                                                }
                                                                                        >
                                                                                                ${invoice.balance.toFixed(2)}
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
                                                                                                {invoice.status}
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <button
                                                                                                        onClick={() =>
                                                                                                                navigate(`/billing/invoices/${invoice.id}`)
                                                                                                        }
                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                        title="View"
                                                                                                >
                                                                                                        <FiEye />
                                                                                                </button>
                                                                                                <button
                                                                                                        onClick={() =>
                                                                                                                navigate(`/billing/invoices/edit/${invoice.id}`)
                                                                                                        }
                                                                                                        className="btn btn-ghost btn-xs"
                                                                                                        title="Edit"
                                                                                                >
                                                                                                        <FiEdit />
                                                                                                </button>
                                                                                                {invoice.balance > 0 && (
                                                                                                        <button
                                                                                                                onClick={() =>
                                                                                                                        navigate(`/billing/invoices/${invoice.id}`)
                                                                                                                }
                                                                                                                className="btn btn-ghost btn-xs text-success"
                                                                                                                title="Add Payment"
                                                                                                        >
                                                                                                                <FiDollarSign />
                                                                                                        </button>
                                                                                                )}
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>
                                )}
                        </div>
                </div>
        );
};

export default InvoiceList;
